import { PropsWithChildren } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  CustomDialogContent,
  CustomDialogFooter,
  CustomDialogTitle,
} from "../ui/custom-dialog";
import { Dialog, DialogHeader } from "../ui/dialog";
import { Form } from "../ui/form";

type DuplicateEntityDialogProps<T extends FieldValues> = PropsWithChildren<{
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  form: UseFormReturn<T>;
  onSubmit: (data: T) => Promise<void>;
  isLoading?: boolean;
  submitLabel?: string;
}>;

function DuplicateEntityDialog<T extends FieldValues>({
  title,
  open,
  onOpenChange,
  form,
  onSubmit,
  isLoading = false,
  submitLabel,
  children,
}: DuplicateEntityDialogProps<T>) {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CustomDialogContent>
        <DialogHeader>
          <CustomDialogTitle>{title}</CustomDialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit((data) => onSubmit(data as T))}>
            <div className="px-6 flex flex-col gap-6 mb-8 mt-3">{children}</div>

            <CustomDialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex flex-1 bg-white"
              >
                {t("actionCancel")}
              </Button>
              <Button type="submit" className="flex flex-1" loading={isLoading}>
                {submitLabel ?? t("duplicate")}
              </Button>
            </CustomDialogFooter>
          </form>
        </Form>
      </CustomDialogContent>
    </Dialog>
  );
}

export default DuplicateEntityDialog;
